import { makeAutoObservable } from 'mobx'
import { isCancel } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ConversionsAnalyticsApi } from 'entities/Analytics'
import { Conversion, ConversionsApi } from 'entities/Conversion'
import { ExportAnalyticsModalStore } from 'widgets/ExportAnalyticsModal'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import { ConversionDetailsChartDataStore } from '../ui/Chart'

export class ConversionDetailsStore {
  private readonly _chartDataStore: ConversionDetailsChartDataStore
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _conversionId: number | null = null
  private _conversionDetail: Conversion | null = null
  private _error: string | null = null
  private _pageLoading = false
  private _chartLoading = false

  constructor(entities: IEntitiesStore) {
    this._chartDataStore = new ConversionDetailsChartDataStore()
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: (loadingType) => this._fetchAnalytics(loadingType),
      additionalFilterTypes: [],
      breakdownFilterTypes: [],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore((params) => {
      if (!this._conversionId) {
        throw new Error('Conversion ID is not available')
      }

      return ConversionsAnalyticsApi.exportAnalyticsByConversionId(this._conversionId, params)
    })

    makeAutoObservable(this)
  }

  init = (conversionId: number) => {
    this._conversionId = conversionId
    this._analyticsFiltersStore.initReactions()
    void this._fetchConversionDetail()
    this._fetchAnalytics('page')
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  private _fetchConversionDetail = async () => {
    if (!this._conversionId) return

    console.log(this._conversionId)

    try {
      const { data } = await ConversionsApi.getConversionById(this._conversionId)

      this._conversionDetail = new Conversion(data)
    } catch (error) {
      if (!isCancel(error)) {
        this._error = 'Failed to load conversion details'
      }
    }
  }

  private _fetchAnalytics = async (loadingType: 'page' | 'chart' = 'page') => {
    if (!this._conversionId) return

    if (loadingType === 'page') {
      this._pageLoading = true
    } else {
      this._chartLoading = true
    }

    try {
      this._error = null
      const { analyticsFiltersParams } = this._analyticsFiltersStore
      const { period } = this._analyticsFiltersStore.chartFilterStore

      this._requestCancelAxios.init()

      const { data } = await ConversionsAnalyticsApi.getConversionAnalytics(
        this._conversionId,
        analyticsFiltersParams,
        {
          cancelToken: this._requestCancelAxios.token,
        }
      )

      this._chartDataStore.setRawData(data, period)
    } catch (error) {
      if (!isCancel(error)) {
        this._error = 'Failed to load analytics data'
      }
    } finally {
      if (loadingType === 'page') {
        this._pageLoading = false
      } else {
        this._chartLoading = false
      }
    }
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get chartFilterStore() {
    return this._analyticsFiltersStore.chartFilterStore
  }

  get chartDataStore() {
    return this._chartDataStore
  }

  get conversionDetail() {
    return this._conversionDetail
  }

  get pageLoading() {
    return this._pageLoading
  }

  get chartLoading() {
    return this._chartLoading
  }

  get error() {
    return this._error
  }

  dispose = () => {
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
    this._exportAnalyticsModalStore.dispose()
    this._chartDataStore.dispose?.()
  }
}
